/**
 * @file apps/web/src/pages/portal/PortalSignoffPage.tsx
 * @description Client portal sign-off page.
 * Clients can formally sign off on documents, test scripts, and the overall project.
 * Sign-offs are append-only (immutable after creation per business rules).
 * Route: /portal/signoff
 */

import { useEffect, useState, useCallback } from 'react';
import {
  PenLine, CheckCircle, Clock, FileText, ClipboardList,
  AlertCircle, Package, Loader2,
} from 'lucide-react';
import { usePortalAuth } from '../../lib/portalAuth.js';
import { signoffApi, documentsApi, testScriptsApi } from '../../lib/api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Signoff {
  id:          string;
  entity_type: string;
  entity_id:   string;
  signed_by:   string;
  signed_at:   string;
  notes?:      string | null;
}

interface SignoffTarget {
  entity_type: string;
  entity_id:   string;
  label:       string;
  sublabel?:   string;
  icon:        React.ReactNode;
  alreadySigned: boolean;
}

// ---------------------------------------------------------------------------
// Sign-off item card
// ---------------------------------------------------------------------------

function SignoffTargetCard({
  target,
  onSign,
  loading,
}: {
  target:  SignoffTarget;
  onSign:  (entityType: string, entityId: string, notes: string) => Promise<void>;
  loading: boolean;
}) {
  const [notes, setNotes]     = useState('');
  const [signing, setSigning] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleSign = async () => {
    setSigning(true);
    try {
      await onSign(target.entity_type, target.entity_id, notes);
      setNotes('');
      setExpanded(false);
    } finally {
      setSigning(false);
    }
  };

  if (target.alreadySigned) {
    return (
      <div className="card p-4 flex items-center gap-3 opacity-75">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-success/10 text-success shrink-0">
          <CheckCircle size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink">{target.label}</p>
          {target.sublabel && (
            <p className="text-[11px] font-mono text-muted mt-0.5">{target.sublabel}</p>
          )}
        </div>
        <span
          className="text-[11px] font-mono px-2 py-0.5 rounded-full"
          style={{ backgroundColor: 'rgba(34,197,94,0.1)', color: 'rgb(34,197,94)' }}
        >
          Signed
        </span>
      </div>
    );
  }

  return (
    <div className={[
      'card overflow-hidden transition-all',
      expanded ? 'ring-2 ring-blue/20' : '',
    ].join(' ')}>
      <div className="p-4 flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: 'var(--sky)', color: 'var(--blue)' }}
        >
          {target.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-ink">{target.label}</p>
          {target.sublabel && (
            <p className="text-[11px] font-mono text-muted mt-0.5">{target.sublabel}</p>
          )}
        </div>
        {expanded ? (
          <button
            onClick={() => setExpanded(false)}
            className="btn-ghost text-sm text-muted"
          >
            Cancel
          </button>
        ) : (
          <button
            onClick={() => setExpanded(true)}
            disabled={loading}
            className="btn-primary text-sm"
          >
            <PenLine size={13} /> Sign Off
          </button>
        )}
      </div>

      {expanded && (
        <div className="border-t border-[rgba(8,17,31,0.06)] p-4 space-y-3">
          <div>
            <label className="label mb-1.5">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any comments or conditions for this sign-off…"
              rows={3}
              className="input text-sm resize-none"
            />
          </div>

          <div
            className="rounded-lg p-3 text-xs text-muted"
            style={{ backgroundColor: 'var(--sky)' }}
          >
            By clicking "Confirm Sign-off", you are formally approving this item.
            This action is <strong className="text-ink">permanent and cannot be undone</strong>.
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSign}
              disabled={signing}
              className="btn-primary text-sm"
            >
              {signing ? (
                <>
                  <Loader2 size={13} className="animate-spin" />
                  Signing…
                </>
              ) : (
                <>
                  <CheckCircle size={13} />
                  Confirm Sign-off
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signoff history item
// ---------------------------------------------------------------------------

function SignoffHistoryItem({ signoff }: { signoff: Signoff }) {
  const icon = {
    document:    <FileText size={13} />,
    test_script: <ClipboardList size={13} />,
    project:     <Package size={13} />,
  }[signoff.entity_type] ?? <CheckCircle size={13} />;

  return (
    <div className="flex items-start gap-3 py-2 border-b border-[rgba(8,17,31,0.04)] last:border-0">
      <div
        className="w-6 h-6 rounded flex items-center justify-center shrink-0 mt-0.5 text-success"
        style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink">
          {signoff.entity_type.replace(/_/g, ' ')} signed off
        </p>
        {signoff.notes && (
          <p className="text-[11px] text-muted mt-0.5 line-clamp-2">{signoff.notes}</p>
        )}
      </div>
      <p className="text-[11px] font-mono text-muted shrink-0">
        {new Date(signoff.signed_at).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric',
        })}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PortalSignoffPage
// ---------------------------------------------------------------------------

export function PortalSignoffPage() {
  const { user, devHeaders } = usePortalAuth();

  const [signoffs, setSignoffs]       = useState<Signoff[]>([]);
  const [targets, setTargets]         = useState<SignoffTarget[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const projectId = user.projectId;

      const [soffs, docs, scripts] = await Promise.all([
        signoffApi.list(projectId, devHeaders) as Promise<Signoff[]>,
        documentsApi.list(projectId, devHeaders) as Promise<{ id: string; title: string; doc_type: string }[]>,
        testScriptsApi.list(projectId, devHeaders) as Promise<{ id: string; title: string }[]>,
      ]);

      setSignoffs(soffs);

      // Build sign-off targets
      const signedIds = new Set(soffs.map((s) => s.entity_id));

      const docTargets: SignoffTarget[] = docs.map((d) => ({
        entity_type:   'document',
        entity_id:     d.id,
        label:         d.title,
        sublabel:      d.doc_type.toUpperCase(),
        icon:          <FileText size={15} />,
        alreadySigned: signedIds.has(d.id),
      }));

      // For scripts, group as a single target (project-level sign-off)
      const scriptTarget: SignoffTarget = {
        entity_type:   'test_script',
        entity_id:     projectId,
        label:         'Test Scripts',
        sublabel:      `${scripts.length} scripts`,
        icon:          <ClipboardList size={15} />,
        alreadySigned: soffs.some(
          (s) => s.entity_type === 'test_script' && s.entity_id === projectId
        ),
      };

      // Overall project sign-off
      const projectTarget: SignoffTarget = {
        entity_type:   'project',
        entity_id:     projectId,
        label:         'Project Go-Live',
        sublabel:      'Final project acceptance',
        icon:          <Package size={15} />,
        alreadySigned: soffs.some(
          (s) => s.entity_type === 'project' && s.entity_id === projectId
        ),
      };

      setTargets([...docTargets, scriptTarget, projectTarget]);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sign-off data');
    } finally {
      setLoading(false);
    }
  }, [user?.projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleSign = async (entityType: string, entityId: string, notes: string) => {
    if (!user) return;
    const created = await signoffApi.create({
      project_id:  user.projectId,
      entity_type: entityType,
      entity_id:   entityId,
      signed_by:   user.userId,
      notes:       notes || null,
    }, devHeaders) as Signoff;

    setSignoffs((prev) => [created, ...prev]);
    setTargets((prev) =>
      prev.map((t) =>
        t.entity_id === entityId ? { ...t, alreadySigned: true } : t
      )
    );
  };

  const pendingTargets = targets.filter((t) => !t.alreadySigned);
  const allSigned      = targets.length > 0 && pendingTargets.length === 0;

  return (
    <div className="p-8 max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="page-title">Sign-off</h1>
        <p className="text-sm text-muted mt-1">
          Formally sign off on deliverables to complete the project.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {/* All signed banner */}
      {!loading && allSigned && (
        <div
          className="card p-4 mb-4 flex items-center gap-3"
          style={{ borderColor: 'rgba(34,197,94,0.3)', backgroundColor: 'rgba(34,197,94,0.05)' }}
        >
          <CheckCircle size={20} className="text-success shrink-0" />
          <div>
            <p className="text-sm font-semibold text-success">All items signed off</p>
            <p className="text-xs text-muted mt-0.5">
              Your project is ready for go-live. Thank you for your participation.
            </p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[1,2,3].map((i) => <div key={i} className="card h-16 bg-gray/60" />)}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Items to sign */}
          {targets.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-sm font-semibold text-ink">
                  Deliverables
                </h2>
                <p className="text-[11px] font-mono text-muted">
                  {targets.filter((t) => t.alreadySigned).length} / {targets.length} signed
                </p>
              </div>

              {/* Progress */}
              <div className="mb-3">
                <div className="h-1.5 rounded-full bg-[rgba(8,17,31,0.06)] overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: targets.length > 0
                        ? `${Math.round((targets.filter((t) => t.alreadySigned).length / targets.length) * 100)}%`
                        : '0%',
                      backgroundColor: 'rgb(34,197,94)',
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                {targets.map((t) => (
                  <SignoffTargetCard
                    key={`${t.entity_type}:${t.entity_id}`}
                    target={t}
                    onSign={handleSign}
                    loading={loading}
                  />
                ))}
              </div>
            </div>
          )}

          {/* History */}
          {signoffs.length > 0 && (
            <div>
              <h2 className="font-display text-sm font-semibold text-ink mb-3">
                Sign-off History
              </h2>
              <div className="card p-4">
                {signoffs.map((s) => (
                  <SignoffHistoryItem key={s.id} signoff={s} />
                ))}
              </div>
            </div>
          )}

          {targets.length === 0 && signoffs.length === 0 && (
            <div className="empty-state py-16">
              <Clock size={32} className="text-muted/30 mb-3 mx-auto" />
              <p className="text-sm text-muted">No deliverables ready for sign-off yet.</p>
              <p className="text-xs text-muted/70 mt-1">
                Your implementation team will let you know when items are ready.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
